import React, { useCallback } from 'react';

import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum } from 'constants/loanActionEditor';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';

const defaultArray = [];

export const DynamicValueInput = ({
  name,
  required = false,
  label,
  className = '',
  disabled = false,
  onChange = null,
  groupBy,
  type = FieldTypesEnum.STRING,
  options = defaultArray,
  filterOptions,
  onGetOptionSelected,
  OptionContent,
  getOptionDisabled,
  withSortedOptions = true,
  maxLength = 100,
  optionKey = 'value',
}) => {
  const inputStyles = {
    marginTop: '16px',
    marginBottom: '8px',
  };
  const classes = {
    input: inputStyles,
  };

  const { form, validationErrors, handleChange, setInForm, validateField } = useFormContext();

  const commonProps = {
    value: _.get(form, name),
    disabled,
    error: !!_.get(validationErrors, name),
    helperText: _.get(validationErrors, name),
    label,
    name,
    required,
  };

  const onAutocompleteChange = useCallback(
    (e, value) => {
      onAutocompleteChangeInForm({ setInForm, validateField })(e, value);
      onChange?.(e, value);
    },
    [onChange, setInForm, validateField],
  );

  // will be more options later
  switch (type) {
    case FieldTypesEnum.SELECT:
      return (
        <AutocompleteCDS
          {...commonProps}
          disableClearable
          OptionContent={OptionContent}
          containerClassName={className}
          dataTestIdInput={loanManagementTestIds.getDynamicValueInputId(name)}
          dataTestIdOption={loanManagementTestIds.getDynamicValueOptionId(name)}
          filterOptions={filterOptions}
          getOptionDisabled={getOptionDisabled}
          groupBy={groupBy}
          isOptionEqualToValue={onGetOptionSelected}
          optionKey={optionKey}
          options={options}
          withSortedOptions={withSortedOptions}
          onChange={onAutocompleteChange}
        />
      );

    case FieldTypesEnum.MULTISELECT:
      return (
        <AutocompleteCDS
          {...commonProps}
          disableClearabled
          multiple
          OptionContent={OptionContent}
          containerClassName={className}
          dataTestIdInput={loanManagementTestIds.getDynamicValueInputId(name)}
          dataTestIdOption={loanManagementTestIds.getDynamicValueOptionId(name)}
          filterOptions={filterOptions}
          groupBy={groupBy}
          isOptionEqualToValue={onGetOptionSelected}
          optionKey={optionKey}
          options={options}
          value={commonProps.value || defaultArray}
          withSortedOptions={withSortedOptions}
          onChange={onAutocompleteChange}
        />
      );

    case FieldTypesEnum.BOOLEAN:
      return (
        <CheckboxCDS
          {...commonProps}
          dataTestId={loanManagementTestIds.getDynamicValueCheckboxId(name)}
          onChange={handleChange}
        />
      );

    default:
      return (
        <TextFieldCDS
          {...commonProps}
          className={`${classes.input} ${className}`}
          data-testid={loanManagementTestIds.getDynamicValueInputId(name)}
          id={name}
          maxLength={maxLength}
          type="text"
          variant="outlined"
          onChange={handleChange}
        />
      );
  }
};

DynamicValueInput.propTypes = {
  ...WithLoadingPropTypes,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  OptionContent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  filterOptions: PropTypes.func,
  getOptionDisabled: PropTypes.func,
  groupBy: PropTypes.func,
  maxLength: PropTypes.number,
  optionKey: PropTypes.string,
  options: PropTypes.array,
  required: PropTypes.bool,
  type: PropTypes.string,
  withSortedOptions: PropTypes.bool,
  onChange: PropTypes.func,
  onGetOptionSelected: PropTypes.func,
};
